/**
 * Affine transform core — brief §3.
 *
 * Matrices use the SVG convention [a, b, c, d, e, f]:
 *   x' = a·x + c·y + e
 *   y' = b·x + d·y + f
 *
 * A TileTransform maps tile-space onto a placement:
 *   M = T(tx, ty) · about-centre(0.5, 0.5)[ R(rotation) · F(flipX, flipY) ]
 * i.e. flips first, then rotation (both about the tile centre), then translation.
 */
import type { TileTransform } from '@/patterns/types';

export type Mat = [number, number, number, number, number, number];

export const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

/** compose(m, n) = m ∘ n (n applied first). */
export function compose(m: Mat, n: Mat): Mat {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function invert(m: Mat): Mat {
  const det = m[0] * m[3] - m[1] * m[2];
  if (Math.abs(det) < 1e-12) throw new Error('Singular transform');
  return [
    m[3] / det,
    -m[1] / det,
    -m[2] / det,
    m[0] / det,
    (m[2] * m[5] - m[3] * m[4]) / det,
    (m[1] * m[4] - m[0] * m[5]) / det,
  ];
}

export function apply(m: Mat, p: [number, number]): [number, number] {
  return [m[0] * p[0] + m[2] * p[1] + m[4], m[1] * p[0] + m[3] * p[1] + m[5]];
}

/**
 * True when the linear part flips handedness (a reflection / glide — odd number
 * of mirrors), i.e. negative determinant. Pure rotations/translations are
 * orientation-preserving.
 *
 * NOTE: this is NOT, on its own, the in/out handle-swap rule. Whether a linked
 * copy swaps its handle slots depends on how the copy was actually traced
 * (edge order), decided geometrically by `edgeLinkCorrespondence` /
 * `linkHandlesSwap`; the determinant sign only agrees with it in the aligned
 * case and is used as a degenerate-shape fallback. It also drives the live
 * draw-preview snap (`computeSnap`), which never persists a link.
 */
export function isOrientationReversing(m: Mat): boolean {
  return m[0] * m[3] - m[1] * m[2] < 0;
}

export function translation(tx: number, ty: number): Mat {
  return [1, 0, 0, 1, tx, ty];
}

export function rotation(theta: number): Mat {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return [c, s, -s, c, 0, 0];
}

export function scale(sx: number, sy: number): Mat {
  return [sx, 0, 0, sy, 0, 0];
}

const CENTRE = 0.5;

export function transformToMatrix(t: TileTransform): Mat {
  if (t.matrix) return t.matrix;
  let m: Mat = translation(-CENTRE, -CENTRE);
  if (t.flipX || t.flipY) {
    m = compose(scale(t.flipX ? -1 : 1, t.flipY ? -1 : 1), m);
  }
  if (t.rotation !== 0) {
    m = compose(rotation(t.rotation), m);
  }
  return compose(translation(t.tx + CENTRE, t.ty + CENTRE), m);
}

/** Convenience constructor with defaults. */
export function tile(tx: number, ty: number, rot = 0, flipX = false, flipY = false): TileTransform {
  return { tx, ty, rotation: rot, flipX, flipY };
}

export function isIdentityTransform(t: TileTransform): boolean {
  if (t.matrix) return isIdentityMatrix(t.matrix);
  return t.tx === 0 && t.ty === 0 && t.rotation === 0 && !t.flipX && !t.flipY;
}

/** True if a matrix is (numerically) the identity. */
export function isIdentityMatrix(m: Mat, eps = 1e-9): boolean {
  return (
    Math.abs(m[0] - 1) < eps &&
    Math.abs(m[1]) < eps &&
    Math.abs(m[2]) < eps &&
    Math.abs(m[3] - 1) < eps &&
    Math.abs(m[4]) < eps &&
    Math.abs(m[5]) < eps
  );
}

/** Build a TileTransform that places the tile via an explicit matrix. */
export function matTransform(matrix: Mat): TileTransform {
  return { tx: 0, ty: 0, rotation: 0, flipX: false, flipY: false, matrix };
}

/** Rotation by `theta` radians about the point (cx, cy). */
export function rotationAbout(theta: number, cx: number, cy: number): Mat {
  return compose(translation(cx, cy), compose(rotation(theta), translation(-cx, -cy)));
}

/**
 * Reflection across the line through point (px, py) with direction (dx, dy).
 * The direction need not be normalised.
 */
export function reflectionLine(px: number, py: number, dx: number, dy: number): Mat {
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const a = ux * ux - uy * uy;
  const b = 2 * ux * uy;
  const c = b;
  const d = uy * uy - ux * ux;
  // translation so the line through (px,py) is fixed
  const e = px - (a * px + c * py);
  const f = py - (b * px + d * py);
  return [a, b, c, d, e, f];
}

/**
 * Glide reflection: reflect across the line through (px, py) along direction
 * (dx, dy), then translate by `dist` along that (unit) direction.
 */
export function glideLine(px: number, py: number, dx: number, dy: number, dist: number): Mat {
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return compose(translation(ux * dist, uy * dist), reflectionLine(px, py, dx, dy));
}
